// The render pipeline: walks the layer stack bottom-to-top, executing each
// generator/filter and compositing results. Per-layer outputs are cached by
// content key so editing layer N's opacity does NOT re-run upstream layers.

import { fnv1a, stableStringify } from '@/lib/hash';
import type { AppState, Layer, LayerKind } from './types';
import { composite } from './composite';

// ---- Registry --------------------------------------------------------------

export type LayerCategory = 'generator' | 'filter';

export type RunContext = {
  width: number;
  height: number;
  seed: number;
  time: number;
};

export type ParamSchemaField =
  | { type: 'number'; label?: string; min?: number; max?: number; step?: number; default: number }
  | { type: 'int'; label?: string; min?: number; max?: number; step?: number; default: number }
  | { type: 'color'; label?: string; default: string }
  | { type: 'bool'; label?: string; default: boolean }
  | { type: 'select'; label?: string; options: readonly string[]; default: string }
  | { type: 'palette'; label?: string; default: string }
  | { type: 'image'; label?: string };

export type ParamSchema = Record<string, ParamSchemaField>;

export type LayerSpec = {
  kind: LayerKind;
  category: LayerCategory;
  /** Whether output depends on RunContext.time. */
  animated: boolean;
  /** Default values for params. */
  defaults: Record<string, unknown>;
  /** Schema for inspector UI generation. */
  schema: ParamSchema;
  /** Generator: input is null. Filter: input is the running composite. */
  run: (
    input: ImageData | null,
    params: Record<string, unknown>,
    ctx: RunContext,
  ) => ImageData | Promise<ImageData>;
  /** Optional human-readable label for the UI. */
  label?: string;
};

const REGISTRY = new Map<LayerKind, LayerSpec>();

export function registerLayer(spec: LayerSpec): void {
  if (REGISTRY.has(spec.kind)) {
    throw new Error(`pipeline: layer kind already registered: ${spec.kind}`);
  }
  REGISTRY.set(spec.kind, spec);
}

export function getLayerSpec(kind: LayerKind): LayerSpec | undefined {
  return REGISTRY.get(kind);
}

export function listSpecs(category?: LayerCategory): LayerSpec[] {
  const all = [...REGISTRY.values()];
  return category ? all.filter((s) => s.category === category) : all;
}

/** Build a Layer with default params for a registered kind. */
export function makeDefaultLayer(kind: LayerKind, id: string): Layer {
  const spec = REGISTRY.get(kind);
  if (!spec) throw new Error(`pipeline: unknown layer kind: ${kind}`);
  return {
    id,
    kind,
    params: { ...spec.defaults },
    blend: 'source-over',
    opacity: 1,
    enabled: true,
    seed: Math.floor(Math.random() * 0x7fffffff),
  };
}

/** Test-only: clear the registry. */
export function __resetRegistryForTests(): void {
  REGISTRY.clear();
}

// ---- Cache keys ------------------------------------------------------------

function layerCacheKey(
  layer: Layer,
  spec: LayerSpec,
  upstreamCompositeKey: string,
  w: number,
  h: number,
  time: number,
): string {
  const tBucket = spec.animated ? time : 0;
  const upstream = spec.category === 'filter' ? upstreamCompositeKey : '';
  return fnv1a(
    `${layer.kind}|${stableStringify(layer.params)}|${layer.seed}|${tBucket}|${w}x${h}|${upstream}`,
  );
}

function compositeKey(
  prevCompositeKey: string,
  layerId: string,
  layerKey: string,
  blend: string,
  opacity: number,
): string {
  return fnv1a(`${prevCompositeKey}|${layerId}|${layerKey}|${blend}|${opacity}`);
}

// ---- Pipeline --------------------------------------------------------------

export type PipelineStats = {
  layersRun: number;
  layersCached: number;
  ms: number;
};

export type PipelineResult = {
  image: ImageData | null;
  stats: PipelineStats;
};

export interface Pipeline {
  render(state: AppState): Promise<PipelineResult>;
  invalidate(layerId?: string): void;
  prune(activeLayerIds: Iterable<string>): void;
  size(): number;
}

export function createPipeline(): Pipeline {
  const cache = new Map<string, { key: string; output: ImageData }>();

  async function render(state: AppState): Promise<PipelineResult> {
    const start = nowMs();
    const { resolution, layers, time } = state;
    const { w, h } = resolution;
    let acc: ImageData | null = null;
    let upstreamCompositeKey = '';
    let layersRun = 0;
    let layersCached = 0;

    const solo = state.soloLayerId;
    for (const layer of layers) {
      if (!layer.enabled || layer.opacity === 0) continue;
      if (solo !== null && layer.id !== solo) continue;
      const spec = REGISTRY.get(layer.kind);
      if (!spec) continue;
      // A filter with no running composite below it is a no-op.
      if (spec.category === 'filter' && acc === null) continue;

      const lkey = layerCacheKey(layer, spec, upstreamCompositeKey, w, h, time);
      const cached = cache.get(layer.id);
      let layerOut: ImageData;
      if (cached !== undefined && cached.key === lkey) {
        layerOut = cached.output;
        layersCached++;
      } else {
        const input = spec.category === 'filter' ? acc : null;
        layerOut = await spec.run(input, layer.params, {
          width: w,
          height: h,
          seed: layer.seed,
          time,
        });
        cache.set(layer.id, { key: lkey, output: layerOut });
        layersRun++;
      }

      acc = composite(acc, layerOut, layer.blend, layer.opacity, w, h);
      upstreamCompositeKey = compositeKey(
        upstreamCompositeKey,
        layer.id,
        lkey,
        layer.blend,
        layer.opacity,
      );
    }

    return {
      image: acc,
      stats: { layersRun, layersCached, ms: nowMs() - start },
    };
  }

  function invalidate(layerId?: string): void {
    if (layerId) cache.delete(layerId);
    else cache.clear();
  }

  function prune(activeLayerIds: Iterable<string>): void {
    const set = new Set(activeLayerIds);
    for (const id of [...cache.keys()]) {
      if (!set.has(id)) cache.delete(id);
    }
  }

  function size(): number {
    return cache.size;
  }

  return { render, invalidate, prune, size };
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
